<script setup lang="ts">
/**
 * H15 — Menu drawer footer modal: Language / Currency
 * Figma Modal `50:10099` (desktop centered 440, radius 8, shadow) ·
 * mobile bottom-sheet `50:10112` (radius 8 8 0 0, grabber 44×2) ·
 * content `50:9893` Language / `50:10127` Currency · MenuRow Style=Select.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { FairIconButton } from '@/ui/primitives/FairIconButton'
import { FairRadioButton } from '@/ui/primitives/FairRadioButton'
import {
  FAIR_HOME_V2_MENU_CURRENCIES,
  FAIR_HOME_V2_MENU_LANGUAGES,
  type FairHomeV2MenuLocaleKind,
} from './fair-home-v2-menu-drawer.data'

const props = withDefaults(
  defineProps<{
    open?: boolean
    kind?: FairHomeV2MenuLocaleKind
    compact?: boolean
    /** Currently applied value (language id / currency code). */
    value?: string
  }>(),
  {
    open: false,
    kind: 'language',
    compact: false,
    value: '',
  },
)

const emit = defineEmits<{
  'update:open': [value: boolean]
  apply: [kind: FairHomeV2MenuLocaleKind, value: string]
}>()

const { t } = useI18n()

/** Draft selection — committed only on Apply (Figma: radio + Apply). */
const draft = ref(props.value)

watch(
  () => [props.open, props.value] as const,
  ([isOpen, value]) => {
    if (isOpen) draft.value = value
  },
  { immediate: true },
)

const options = computed(() =>
  props.kind === 'language'
    ? FAIR_HOME_V2_MENU_LANGUAGES.map((option) => ({
        id: option.id,
        label: option.label,
        description: option.description,
      }))
    : FAIR_HOME_V2_MENU_CURRENCIES.map((option) => ({
        id: option.id,
        label: option.label,
        description: option.description,
      })),
)

const title = computed(() =>
  props.kind === 'language'
    ? t('fair_home_v2.menu.language')
    : t('fair_home_v2.menu.currency'),
)

const radioName = computed(() => `fair-home-v2-menu-${props.kind}`)

const rootClass = computed(() => ({
  'fair-home-v2-menu-modal': true,
  'fair-home-v2-menu-modal--compact': props.compact,
  'fair-home-v2-menu-modal--desktop': !props.compact,
}))

function close() {
  emit('update:open', false)
}

function apply() {
  emit('apply', props.kind, draft.value)
  close()
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.stopPropagation()
    close()
  }
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeydown, true)
    } else {
      document.removeEventListener('keydown', handleKeydown, true)
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown, true)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="fair-h2-menu-modal">
      <div
        v-if="open"
        :class="rootClass"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        data-unit="H15"
        :data-figma="compact ? '50:10112' : '50:10100'"
      >
        <button
          type="button"
          class="fair-home-v2-menu-modal__mask"
          :aria-label="t('fair_home_v2.menu.close_modal')"
          @click="close"
        />

        <div class="fair-home-v2-menu-modal__panel">
          <span
            v-if="compact"
            class="fair-home-v2-menu-modal__grabber"
            aria-hidden="true"
          />

          <header class="fair-home-v2-menu-modal__header">
            <span
              v-if="compact"
              class="fair-home-v2-menu-modal__spacer"
              aria-hidden="true"
            />
            <h2 class="fair-home-v2-menu-modal__title">
              {{ title }}
            </h2>
            <FairIconButton
              icon="close"
              :size="compact ? 'mobile' : 'medium'"
              :aria-label="t('fair_home_v2.menu.close_modal')"
              @click="close"
            />
          </header>
          <div
            v-if="!compact"
            class="fair-home-v2-menu-modal__rule"
            aria-hidden="true"
          />

          <div class="fair-home-v2-menu-modal__content">
            <ul class="fair-home-v2-menu-modal__list">
              <li
                v-for="option in options"
                :key="option.id"
              >
                <FairRadioButton
                  v-model="draft"
                  :value="option.id"
                  :name="radioName"
                  :input-id="`${radioName}-${option.id}`"
                  class="fair-home-v2-menu-modal__row"
                  :class="{ 'fair-home-v2-menu-modal__row--selected': draft === option.id }"
                >
                  <span class="fair-home-v2-menu-modal__row-text">
                    <span class="fair-home-v2-menu-modal__row-label">{{ option.label }}</span>
                    <span class="fair-home-v2-menu-modal__row-caption">{{ option.description }}</span>
                  </span>
                </FairRadioButton>
              </li>
            </ul>
          </div>

          <footer class="fair-home-v2-menu-modal__footer">
            <button
              type="button"
              class="fair-home-v2-menu-modal__apply"
              @click="apply"
            >
              {{ t('fair_home_v2.menu.apply') }}
            </button>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
